import { Box, Button, Typography } from '@mui/material';
import {
  AppName,
  HomeAppBar,
  InfiniteTable,
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { format } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { BrandKey } from '../../__generated__/queries-web';
import {
  DeleteFlashSenderProgramMutation,
  DeleteFlashSenderProgramMutationVariables,
  FlashSenderProgram,
  FlashSenderProgramStatus,
} from '../../__generated__/queries-newsletter';
import { OneDrawerMenu } from '../../components/AppBar';
import { BrandSwitch } from '../../components/BrandSwitch';
import { auth } from '../../utils/auth';
import {
  DELETE_FLASH_SENDER_PROGRAM,
  FLASH_SENDER_PROGRAMS,
  useFlashSenderPrograms,
} from './flashSenderProgram.newsletter.graphql';
import { useMutation } from '@apollo/client';

export const NLFlashList: FC = () => {
  const { pushNotification } = useNotification();
  const [brandKey, setBrandKey] = useState<BrandKey | undefined>(undefined);
  const [brandKeyUrl, setBrandKeyUrl] = useQueryParam('brand', StringParam);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { data, loading, error } = useFlashSenderPrograms();
  const [deleteFlashSenderProgram, { loading: deleting }] = useMutation<
    DeleteFlashSenderProgramMutation,
    DeleteFlashSenderProgramMutationVariables
  >(DELETE_FLASH_SENDER_PROGRAM, {
    refetchQueries: [FLASH_SENDER_PROGRAMS],
    onCompleted: () => {
      pushNotification({
        message: 'Programmation annulée avec succès',
        type: NotificationTypeEnum.success,
      });
    },
    onError: (mutationError) => {
      pushNotification({
        message: mutationError.message,
        type: NotificationTypeEnum.error,
      });
    },
  });

  useEffect(() => {
    brandKeyUrl && setBrandKey(brandKeyUrl as BrandKey);
  }, [brandKeyUrl]);

  useEffect(() => {
    setBrandKeyUrl(brandKey, 'replaceIn');
  }, [brandKey, setBrandKeyUrl]);

  const scheduledPrograms = data?.flashSenderPrograms.filter(
    (program) =>
      program.status === FlashSenderProgramStatus.SCHEDULED &&
      (!brandKey || `${program.brandKey}` === `${brandKey}`),
  );

  return (
    <>
      <HomeAppBar
        currentUser={auth.user}
        disconnectUser={auth.logout}
        appDrawerMenu={
          <OneDrawerMenu currentBrand={brandKey} app={'NL Flash' as AppName} />
        }
        brandMenu={
          <BrandSwitch brandKey={brandKey} setBrandKey={setBrandKey} />
        }
      />

      <Box sx={{ p: 4, pb: 0 }}>
        <Typography variant="h5">NL Flash</Typography>
      </Box>

      <Box sx={{ width: '100vw', height: 'calc(100vh - 128px)' }}>
        <InfiniteTable
          list={scheduledPrograms}
          loading={loading}
          error={error}
          columns={[
            {
              label: 'Objet',
              key: 'subject',
              width: 320,
              flexGrow: 1,
            },
            {
              label: 'Marque',
              key: 'brandKey',
              width: 120,
            },
            {
              label: 'Status',
              key: 'status',
              width: 140,
              cellDataGetter: ({ rowData }: { rowData: FlashSenderProgram }) =>
                rowData.status === FlashSenderProgramStatus.SCHEDULED
                  ? 'Programmé'
                  : rowData.status,
            },
            {
              label: 'Date de programmation',
              key: 'scheduledAt',
              width: 220,
              cellDataGetter: ({ rowData }: { rowData: FlashSenderProgram }) =>
                rowData.scheduledAt
                  ? format(
                      Date.parse(rowData.scheduledAt),
                      'yyyy-MM-dd HH:mm:ss',
                    )
                  : '-',
            },
            {
              label: '',
              key: 'action',
              width: 140,
              cellRenderer: (rowData: FlashSenderProgram) => (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={deleting && deletingId === rowData.id}
                    onClick={async (event) => {
                      event.stopPropagation();
                      setDeletingId(rowData.id);
                      try {
                        await deleteFlashSenderProgram({
                          variables: {
                            where: { id: rowData.id },
                          },
                          refetchQueries: [FLASH_SENDER_PROGRAMS],
                        });
                      } finally {
                        setDeletingId(null);
                      }
                    }}
                  >
                    {deleting && deletingId === rowData.id
                      ? 'Deleting'
                      : 'Annuler'}
                  </Button>
                </Box>
              ),
            },
          ]}
        />
      </Box>
    </>
  );
};
